// src/pages/DelegationPage.jsx — Mailbox Delegation Audit
// Auth: MSAL SSO (delegated) — no client_credentials, no manual secret entry
// Data: SharePoint list via Microsoft Graph

import React, { useState, useEffect, useRef, useCallback } from "react";
import { graphRequest } from "../services/graphService";
import { GRAPH_BASE } from "../authConfig";

const SITE_ID = "imagroupmgmt.sharepoint.com,9d963613-7ac6-463a-9269-8a78c00145e6,985cd7b6-cb16-4674-9a26-04f0954a90a6";
const LIST_ID = "0e76a8fa-ec28-474f-8bc2-de9b089eae9d";
const PAGE_SIZE = 25;

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getCurrentQuarter() {
  const now = new Date();
  const q = Math.ceil((now.getMonth() + 1) / 3);
  return `Q${q}-${now.getFullYear()}`;
}

function Badge({ status }) {
  const map = {
    "Pending Review": { bg: "rgba(255,158,24,0.12)", fg: "#FF9E18", dot: "#FF9E18" },
    "Keep":           { bg: "rgba(106,191,75,0.12)",  fg: "#6ABF4B", dot: "#6ABF4B" },
    "Remove":         { bg: "rgba(220,53,69,0.12)",   fg: "#dc3545", dot: "#dc3545" },
    "Reassign":       { bg: "rgba(0,187,180,0.12)",   fg: "#00BBB4", dot: "#00BBB4" },
  };
  const s = map[status] || map["Pending Review"];
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "2px 8px", borderRadius: 99,
      background: s.bg, color: s.fg, fontSize: 10, fontWeight: 600,
    }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: s.dot }}/>
      {status || "Pending Review"}
    </span>
  );
}

// ─── Donut chart (SVG) ────────────────────────────────────────────────────────
function Donut({ data, total, size = 140 }) {
  const r = 54, cx = size / 2, cy = size / 2, sw = 20, circ = 2 * Math.PI * r;
  let off = 0;
  const paths = data.map((d, i) => {
    const dash = (d.count / total) * circ;
    const el = (
      <circle key={i} cx={cx} cy={cy} r={r} fill="none"
        stroke={d.color} strokeWidth={sw}
        strokeDasharray={`${dash} ${circ - dash}`}
        strokeDashoffset={-off}
        transform={`rotate(-90 ${cx} ${cy})`}/>
    );
    off += dash;
    return el;
  });
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--b2)" strokeWidth={sw}/>
      {paths}
      <text x={cx} y={cy - 6} textAnchor="middle" fill="var(--tx)" fontFamily="var(--sans)" fontSize={24} fontWeight={900}>{total}</text>
      <text x={cx} y={cy + 12} textAnchor="middle" fill="var(--tx3)" fontFamily="var(--mono)" fontSize={10}>delegations</text>
    </svg>
  );
}

// ─── Bar row ─────────────────────────────────────────────────────────────────
function BarRow({ label, count, max, color }) {
  const pct = max > 0 ? Math.round((count / max) * 100) : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
      <div style={{ width: 130, fontSize: 11, color: "var(--tx2)", textAlign: "right", flexShrink: 0 }}>{label}</div>
      <div style={{ flex: 1, background: "var(--s3)", borderRadius: 4, height: 18, overflow: "hidden" }}>
        <div style={{
          width: `${pct}%`, height: "100%", background: color, borderRadius: 4,
          display: "flex", alignItems: "center", paddingLeft: 8,
          transition: "width .4s ease", minWidth: count > 0 ? 30 : 0,
        }}>
          {pct > 6 && <span style={{ fontSize: 10, color: "#fff", fontFamily: "var(--mono)" }}>{count}</span>}
        </div>
      </div>
      <div style={{ width: 30, fontFamily: "var(--mono)", fontSize: 11, color: "var(--tx3)" }}>{count}</div>
    </div>
  );
}

// ─── Section card ─────────────────────────────────────────────────────────────
function Card({ title, right, children }) {
  return (
    <div style={{
      background: "var(--s1)", border: "1px solid var(--b1)",
      borderRadius: 10, overflow: "hidden", marginBottom: 16,
    }}>
      <div style={{
        padding: "12px 16px", borderBottom: "1px solid var(--b1)",
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: "var(--tx2)" }}>{title}</span>
        {right && <span style={{ fontFamily: "var(--mono)", fontSize: 11, color: "var(--tx3)" }}>{right}</span>}
      </div>
      {children}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export function DelegationPage({ user }) {
  const [allData,  setAllData]  = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");
  const [view,     setView]     = useState("overview");
  const [quarter,  setQuarter]  = useState(getCurrentQuarter());

  // Remove queue state
  const [removeSearch, setRemoveSearch] = useState("");
  const [removePage,   setRemovePage]   = useState(1);

  // ── Fetch SharePoint list via delegated Graph token ───────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      let url = `${GRAPH_BASE}/sites/${SITE_ID}/lists/${LIST_ID}/items?$expand=fields&$top=999`;
      let items = [];
      while (url) {
        const data = await graphRequest(url);
        items = items.concat(data?.value || []);
        url = data?.["@odata.nextLink"] || null;
      }
      setAllData(items.map(i => ({
        id:               i.id,
        mailbox:          i.fields?.Mailbox          || "",
        mailboxEmail:     i.fields?.MailboxEmail     || "",
        mailboxCompany:   i.fields?.MailboxCompany   || "",
        delegate:         i.fields?.Delegate         || "",
        delegateEmail:    i.fields?.DelegateEmail    || "",
        delegateCompany:  i.fields?.DelegateCompany  || "",
        delegateJobTitle: i.fields?.DelegateJobTitle || "",
        permissionType:   i.fields?.PermissionType   || "",
        approverName:     i.fields?.ApproverName     || "",
        approverEmail:    i.fields?.ApproverEmail    || "",
        approvalTier:     i.fields?.ApprovalTier     || "",
        approvalStatus:   i.fields?.ApprovalStatus   || "Pending Review",
        auditQuarter:     i.fields?.AuditQuarter     || "",
        dateAudited:      i.fields?.DateAudited      || "",
        reviewedBy:       i.fields?.ReviewedBy       || "",
        reviewDate:       i.fields?.ReviewDate       || "",
        notes:            i.fields?.Notes            || "",
        reassignedFrom:   i.fields?.ReassignedFrom   || "",
        removedDate:      i.fields?.RemovedDate      || "",
      })));
    } catch (e) {
      setError(e.message || "Failed to load delegation data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Quarter filter ────────────────────────────────────────────────────────────
  const quarters = [...new Set(allData.map(d => d.auditQuarter).filter(Boolean))].sort();
  const data = allData.filter(d => !d.auditQuarter || d.auditQuarter === quarter);

  // ── Overview analytics ────────────────────────────────────────────────────────
  const total    = data.length;
  const pending  = data.filter(d => d.approvalStatus === "Pending Review").length;
  const keep     = data.filter(d => d.approvalStatus === "Keep").length;
  const remove   = data.filter(d => d.approvalStatus === "Remove").length;
  const reassign = data.filter(d => d.approvalStatus === "Reassign").length;
  const reviewed = keep + remove + reassign;
  const pct      = total > 0 ? Math.round((reviewed / total) * 100) : 0;

  const donutData = [
    { label: "Pending Review", count: pending,  color: "#FF9E18" },
    { label: "Keep",           count: keep,     color: "#6ABF4B" },
    { label: "Remove",         count: remove,   color: "#dc3545" },
    { label: "Reassign",       count: reassign, color: "#00BBB4" },
  ].filter(d => d.count > 0);

  const byType = {};
  data.forEach(d => { byType[d.permissionType] = (byType[d.permissionType] || 0) + 1; });
  const typeMax = Math.max(...Object.values(byType), 1);
  const typeColors = { "Full Access": "#004D71", "Send As": "#00B2E3", "Send on Behalf": "#00BBB4" };

  // ── By company analytics ──────────────────────────────────────────────────────
  const companies = {};
  data.forEach(d => {
    const co = d.delegateCompany || "Unknown";
    if (!companies[co]) companies[co] = { total: 0, keep: 0, remove: 0, reassign: 0, pending: 0 };
    companies[co].total++;
    const s = d.approvalStatus;
    if (s === "Keep")    companies[co].keep++;
    else if (s === "Remove")   companies[co].remove++;
    else if (s === "Reassign") companies[co].reassign++;
    else                       companies[co].pending++;
  });

  // ── Remove queue ──────────────────────────────────────────────────────────────
  const removeData = data.filter(d => d.approvalStatus === "Remove");
  const filteredRemove = removeSearch
    ? removeData.filter(d =>
        d.delegate.toLowerCase().includes(removeSearch.toLowerCase()) ||
        d.mailbox.toLowerCase().includes(removeSearch.toLowerCase()) ||
        d.delegateEmail.toLowerCase().includes(removeSearch.toLowerCase()))
    : removeData;
  const removeTotalPages = Math.max(1, Math.ceil(filteredRemove.length / PAGE_SIZE));
  const removePageData   = filteredRemove.slice((removePage - 1) * PAGE_SIZE, removePage * PAGE_SIZE);
  const notDone = removeData.filter(d => !d.removedDate).length;
  const done    = removeData.filter(d => !!d.removedDate).length;

  // ── History analytics ─────────────────────────────────────────────────────────
  const byQ = {};
  allData.forEach(d => {
    const q = d.auditQuarter || "Unknown";
    if (!byQ[q]) byQ[q] = { total: 0, keep: 0, remove: 0, reassign: 0, pending: 0 };
    byQ[q].total++;
    const s = d.approvalStatus;
    if (s === "Keep")    byQ[q].keep++;
    else if (s === "Remove")   byQ[q].remove++;
    else if (s === "Reassign") byQ[q].reassign++;
    else                       byQ[q].pending++;
  });
  const qKeys = Object.keys(byQ).sort();
  const maxQTotal = Math.max(...Object.values(byQ).map(q => q.total), 1);

  // ── Styles ────────────────────────────────────────────────────────────────────
  const subTabStyle = id => ({
    padding: "6px 14px", borderRadius: "var(--r)",
    border: "1px solid " + (view === id ? "var(--b2)" : "transparent"),
    background: view === id ? "var(--s3)" : "transparent",
    color: view === id ? "var(--tx)" : "var(--tx3)",
    fontFamily: "var(--sans)", fontSize: 11, fontWeight: view === id ? 600 : 400,
    cursor: "pointer", transition: "all .15s",
  });

  const thStyle = {
    padding: "8px 10px", textAlign: "left", fontSize: 10,
    color: "var(--tx3)", fontFamily: "var(--mono)", letterSpacing: ".05em",
    textTransform: "uppercase", background: "var(--s2)",
    borderBottom: "1px solid var(--b1)", whiteSpace: "nowrap",
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>

      {/* Controls bar */}
      <div style={{
        padding: "10px 20px", background: "var(--s1)", borderBottom: "1px solid var(--b1)",
        display: "flex", alignItems: "center", gap: 10, flexShrink: 0, flexWrap: "wrap",
      }}>
        <div style={{ fontFamily: "var(--mono)", fontSize: 10, color: "var(--tx3)", letterSpacing: ".05em" }}>QUARTER</div>
        <select value={quarter} onChange={e => setQuarter(e.target.value)} style={{
          background: "var(--s2)", border: "1px solid var(--b2)", borderRadius: "var(--r)",
          padding: "5px 10px", fontSize: 11, color: "var(--tx)", fontFamily: "var(--sans)",
        }}>
          <option value={getCurrentQuarter()}>{getCurrentQuarter()} (current)</option>
          {quarters.filter(q => q !== getCurrentQuarter()).map(q => (
            <option key={q} value={q}>{q}</option>
          ))}
        </select>

        <button onClick={loadData} disabled={loading} style={{
          padding: "5px 14px",
          background: loading ? "var(--b2)" : "var(--s2)",
          border: "1px solid var(--b2)", borderRadius: "var(--r)",
          color: loading ? "var(--tx3)" : "var(--tx2)",
          fontFamily: "var(--sans)", fontSize: 11, cursor: loading ? "not-allowed" : "pointer",
          display: "flex", alignItems: "center", gap: 6,
        }}>
          {loading && <span style={{
            width: 10, height: 10, border: "1.5px solid var(--b3)",
            borderTopColor: "var(--primary)", borderRadius: "50%",
            animation: "spin .7s linear infinite",
          }}/>}
          {loading ? "Loading…" : "↻ Refresh"}
        </button>

        {!loading && allData.length > 0 && (
          <span style={{ fontFamily: "var(--mono)", fontSize: 11, color: "var(--tx3)" }}>
            {total} records · {allData.length} total
          </span>
        )}

        {/* Sub-nav */}
        <div style={{ display: "flex", gap: 3, marginLeft: "auto" }}>
          {[
            { id: "overview", label: "Overview" },
            { id: "company",  label: "By Company" },
            { id: "remove",   label: `Remove Queue${removeData.length > 0 ? ` (${removeData.length})` : ""}` },
            { id: "history",  label: "History" },
          ].map(({ id, label }) => (
            <button key={id} onClick={() => setView(id)} style={subTabStyle(id)}>{label}</button>
          ))}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={{
          margin: "12px 20px", padding: "10px 14px",
          background: "var(--red-dim)", border: "1px solid rgba(224,82,82,.3)",
          borderRadius: "var(--r)", fontSize: 12, color: "var(--red)", flexShrink: 0,
        }}>
          ⚠ {error}
          <button onClick={loadData} style={{
            marginLeft: 12, background: "none", border: "none",
            color: "var(--red)", cursor: "pointer", fontSize: 11, textDecoration: "underline",
          }}>Retry</button>
        </div>
      )}

      {/* Loading state */}
      {loading && allData.length === 0 && (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14 }}>
          <div style={{ width: 32, height: 32, border: "3px solid var(--b2)", borderTopColor: "#6ABF4B", borderRadius: "50%", animation: "spin .8s linear infinite" }}/>
          <div style={{ fontFamily: "var(--mono)", fontSize: 12, color: "var(--tx3)" }}>Loading delegation audit data…</div>
        </div>
      )}

      {/* Content */}
      {!loading && allData.length > 0 && (
        <div style={{ flex: 1, overflowY: "auto", padding: "20px" }}>

          {/* ── OVERVIEW ── */}
          {view === "overview" && (
            <>
              {/* KPIs */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 16 }}>
                {[
                  { label: "Total Delegations",  value: total,    color: "#00B2E3", sub: quarter },
                  { label: "Pending Review",      value: pending,  color: "#FF9E18", sub: "awaiting decision" },
                  { label: "Approved Keep",       value: keep,     color: "#6ABF4B", sub: "delegations confirmed" },
                  { label: "Approved Remove",     value: remove,   color: "#dc3545", sub: "pending execution" },
                  { label: "Reassigned",          value: reassign, color: "#00BBB4", sub: "rerouted" },
                ].map(({ label, value, color, sub }) => (
                  <div key={label} style={{
                    background: "var(--s1)", border: "1px solid var(--b1)",
                    borderRadius: 10, padding: "14px 16px",
                    borderTop: `3px solid ${color}`,
                  }}>
                    <div style={{ fontSize: 10, color: "var(--tx3)", marginBottom: 4 }}>{label}</div>
                    <div style={{ fontSize: 26, fontWeight: 800, color: "var(--tx)", lineHeight: 1 }}>{value.toLocaleString()}</div>
                    <div style={{ fontSize: 10, color: "var(--tx3)", marginTop: 4 }}>{sub}</div>
                  </div>
                ))}
              </div>

              {/* Progress bar */}
              <div style={{
                background: "var(--s1)", border: "1px solid var(--b1)",
                borderRadius: 10, padding: "16px 18px", marginBottom: 16,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "var(--tx2)" }}>Audit Completion — {quarter}</span>
                  <span style={{ fontFamily: "var(--mono)", fontSize: 14, fontWeight: 700, color: pct >= 80 ? "#6ABF4B" : pct >= 50 ? "#FF9E18" : "#e05252" }}>
                    {pct}%
                  </span>
                </div>
                <div style={{ background: "var(--s3)", borderRadius: 99, height: 8, overflow: "hidden" }}>
                  <div style={{
                    width: `${pct}%`, height: "100%", borderRadius: 99,
                    background: pct >= 80 ? "#6ABF4B" : pct >= 50 ? "#FF9E18" : "#00B2E3",
                    transition: "width .6s ease",
                  }}/>
                </div>
                <div style={{ display: "flex", gap: 16, marginTop: 8, fontSize: 11, color: "var(--tx3)" }}>
                  <span>{reviewed} reviewed</span>
                  <span>·</span>
                  <span>{pending} pending</span>
                  <span>·</span>
                  <span>{total} total</span>
                </div>
              </div>

              {/* Charts */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <Card title="Decision Breakdown">
                  <div style={{ padding: 16, display: "flex", alignItems: "center", gap: 20 }}>
                    {total > 0 ? <Donut data={donutData} total={total}/> : <div style={{ color: "var(--tx3)", fontSize: 12 }}>No data</div>}
                    <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
                      {donutData.map(d => (
                        <div key={d.label} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11 }}>
                          <span style={{ width: 8, height: 8, borderRadius: "50%", background: d.color, flexShrink: 0 }}/>
                          <span style={{ flex: 1, color: "var(--tx2)" }}>{d.label}</span>
                          <span style={{ fontFamily: "var(--mono)", color: "var(--tx3)", fontSize: 10 }}>{d.count}</span>
                          <span style={{ fontFamily: "var(--mono)", color: "var(--tx3)", fontSize: 10, minWidth: 28 }}>
                            {total > 0 ? Math.round((d.count / total) * 100) : 0}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>

                <Card title="By Permission Type">
                  <div style={{ padding: 16 }}>
                    {Object.entries(byType).sort((a, b) => b[1] - a[1]).map(([t, c]) => (
                      <BarRow key={t} label={t} count={c} max={typeMax} color={typeColors[t] || "var(--primary)"}/>
                    ))}
                    {Object.keys(byType).length === 0 && (
                      <div style={{ color: "var(--tx3)", fontSize: 11 }}>No data</div>
                    )}
                  </div>
                </Card>
              </div>
            </>
          )}

          {/* ── BY COMPANY ── */}
          {view === "company" && (
            <Card title="Response Rate by Company" right={`${Object.keys(companies).length} companies`}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {["Company", "Total", "Keep", "Remove", "Reassign", "Pending", "Completion"].map(h => (
                        <th key={h} style={thStyle}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(companies).sort((a, b) => b[1].total - a[1].total).map(([co, c], i) => {
                      const rev = c.keep + c.remove + c.reassign;
                      const cp  = c.total > 0 ? Math.round((rev / c.total) * 100) : 0;
                      return (
                        <tr key={co} style={{ background: i % 2 === 0 ? "transparent" : "var(--s2)", borderBottom: "1px solid var(--b1)" }}>
                          <td style={{ padding: "9px 10px", fontWeight: 700, fontSize: 11, color: "var(--tx)" }}>{co}</td>
                          <td style={{ padding: "9px 10px", fontFamily: "var(--mono)", fontSize: 11, color: "#00B2E3" }}>{c.total}</td>
                          <td style={{ padding: "9px 10px", fontFamily: "var(--mono)", fontSize: 11, color: "#6ABF4B" }}>{c.keep}</td>
                          <td style={{ padding: "9px 10px", fontFamily: "var(--mono)", fontSize: 11, color: "#dc3545" }}>{c.remove}</td>
                          <td style={{ padding: "9px 10px", fontFamily: "var(--mono)", fontSize: 11, color: "#00BBB4" }}>{c.reassign}</td>
                          <td style={{ padding: "9px 10px", fontFamily: "var(--mono)", fontSize: 11, color: "#FF9E18" }}>{c.pending}</td>
                          <td style={{ padding: "9px 10px", minWidth: 140 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div style={{ flex: 1, background: "var(--s3)", borderRadius: 4, height: 6, overflow: "hidden" }}>
                                <div style={{ width: `${cp}%`, height: "100%", background: "#6ABF4B", borderRadius: 4 }}/>
                              </div>
                              <span style={{ fontFamily: "var(--mono)", fontSize: 10, color: "var(--tx3)", minWidth: 28 }}>{cp}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* ── REMOVE QUEUE ── */}
          {view === "remove" && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 16 }}>
                {[
                  { label: "Total to Remove",    value: removeData.length, color: "#dc3545", sub: "approved for removal" },
                  { label: "Pending Execution",  value: notDone,           color: "#FF9E18", sub: "not yet removed" },
                  { label: "Completed",          value: done,              color: "#6ABF4B", sub: "successfully removed" },
                ].map(({ label, value, color, sub }) => (
                  <div key={label} style={{
                    background: "var(--s1)", border: "1px solid var(--b1)",
                    borderRadius: 10, padding: "14px 16px", borderTop: `3px solid ${color}`,
                  }}>
                    <div style={{ fontSize: 10, color: "var(--tx3)", marginBottom: 4 }}>{label}</div>
                    <div style={{ fontSize: 26, fontWeight: 800, color: "var(--tx)", lineHeight: 1 }}>{value}</div>
                    <div style={{ fontSize: 10, color: "var(--tx3)", marginTop: 4 }}>{sub}</div>
                  </div>
                ))}
              </div>

              <Card title="Remove Queue" right={`${filteredRemove.length} items`}>
                <div style={{ padding: "10px 14px", borderBottom: "1px solid var(--b1)" }}>
                  <input
                    value={removeSearch}
                    onChange={e => { setRemoveSearch(e.target.value); setRemovePage(1); }}
                    placeholder="Search delegate or mailbox…"
                    style={{
                      width: "100%", background: "var(--s2)", border: "1px solid var(--b2)",
                      borderRadius: "var(--r)", padding: "6px 10px",
                      fontSize: 11, color: "var(--tx)", fontFamily: "var(--sans)", outline: "none",
                    }}
                  />
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 700 }}>
                    <thead>
                      <tr>
                        {["Delegate", "Email", "Mailbox", "Access Type", "Approved By", "Review Date", "Notes", "Status"].map(h => (
                          <th key={h} style={thStyle}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {removePageData.length === 0
                        ? <tr><td colSpan={8} style={{ padding: "32px", textAlign: "center", color: "var(--tx3)", fontSize: 12 }}>No removals approved yet</td></tr>
                        : removePageData.map((d, i) => (
                          <tr key={d.id || i} style={{ background: i % 2 === 0 ? "transparent" : "var(--s2)", borderBottom: "1px solid var(--b1)" }}>
                            <td style={{ padding: "8px 10px", fontWeight: 700, fontSize: 11, color: "var(--tx)", whiteSpace: "nowrap" }}>{d.delegate}</td>
                            <td style={{ padding: "8px 10px", fontSize: 11, color: "var(--tx3)" }}>{d.delegateEmail}</td>
                            <td style={{ padding: "8px 10px", fontSize: 11, color: "var(--tx2)" }}>{d.mailbox}</td>
                            <td style={{ padding: "8px 10px" }}>
                              <span style={{
                                display: "inline-block", padding: "2px 8px", borderRadius: 99,
                                background: "var(--primary-dim)", border: "1px solid rgba(0,178,227,0.2)",
                                color: "var(--primary)", fontSize: 10, fontWeight: 600, whiteSpace: "nowrap",
                              }}>{d.permissionType}</span>
                            </td>
                            <td style={{ padding: "8px 10px", fontSize: 11, color: "var(--tx2)", whiteSpace: "nowrap" }}>{d.reviewedBy}</td>
                            <td style={{ padding: "8px 10px", fontSize: 10, color: "var(--tx3)", fontFamily: "var(--mono)", whiteSpace: "nowrap" }}>
                              {d.reviewDate ? d.reviewDate.split("T")[0] : ""}
                            </td>
                            <td style={{ padding: "8px 10px", fontSize: 11, color: "var(--tx3)", maxWidth: 160 }}>
                              <span title={d.notes}>{d.notes ? d.notes.substring(0, 35) + (d.notes.length > 35 ? "…" : "") : "—"}</span>
                            </td>
                            <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>
                              {d.removedDate
                                ? <Badge status="Keep"/>  // reuse green badge for "Done"
                                : <Badge status="Pending Review"/>}
                            </td>
                          </tr>
                        ))
                      }
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {removeTotalPages > 1 && (
                  <div style={{
                    padding: "10px 14px", borderTop: "1px solid var(--b1)",
                    display: "flex", alignItems: "center", gap: 6,
                    justifyContent: "space-between",
                  }}>
                    <span style={{ fontFamily: "var(--mono)", fontSize: 11, color: "var(--tx3)" }}>
                      {(removePage - 1) * PAGE_SIZE + 1}–{Math.min(removePage * PAGE_SIZE, filteredRemove.length)} of {filteredRemove.length}
                    </span>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button disabled={removePage <= 1} onClick={() => setRemovePage(p => p - 1)} style={{
                        padding: "4px 10px", background: "var(--s2)", border: "1px solid var(--b2)",
                        borderRadius: "var(--r)", cursor: removePage <= 1 ? "not-allowed" : "pointer",
                        color: "var(--tx3)", fontSize: 11,
                      }}>←</button>
                      {Array.from({ length: Math.min(removeTotalPages, 7) }, (_, j) => j + 1).map(n => (
                        <button key={n} onClick={() => setRemovePage(n)} style={{
                          padding: "4px 10px", borderRadius: "var(--r)", fontSize: 11,
                          background: n === removePage ? "var(--primary)" : "var(--s2)",
                          border: "1px solid " + (n === removePage ? "var(--primary)" : "var(--b2)"),
                          color: n === removePage ? "#fff" : "var(--tx3)", cursor: "pointer",
                        }}>{n}</button>
                      ))}
                      <button disabled={removePage >= removeTotalPages} onClick={() => setRemovePage(p => p + 1)} style={{
                        padding: "4px 10px", background: "var(--s2)", border: "1px solid var(--b2)",
                        borderRadius: "var(--r)", cursor: removePage >= removeTotalPages ? "not-allowed" : "pointer",
                        color: "var(--tx3)", fontSize: 11,
                      }}>→</button>
                    </div>
                  </div>
                )}
              </Card>
            </>
          )}

          {/* ── HISTORY ── */}
          {view === "history" && (
            <>
              {/* Trend chart */}
              <Card title="Quarter over Quarter — Delegation Volume">
                <div style={{ padding: 16 }}>
                  <div style={{ display: "flex", gap: 16, marginBottom: 12, flexWrap: "wrap" }}>
                    {[
                      { label: "Keep",    color: "#6ABF4B" },
                      { label: "Remove",  color: "#dc3545" },
                      { label: "Reassign",color: "#00BBB4" },
                      { label: "Pending", color: "#FF9E18" },
                    ].map(({ label, color }) => (
                      <div key={label} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "var(--tx3)" }}>
                        <span style={{ width: 8, height: 8, borderRadius: "50%", background: color }}/>
                        {label}
                      </div>
                    ))}
                  </div>
                  {qKeys.length > 0 ? (
                    <div style={{ display: "flex", gap: 16, alignItems: "flex-end", height: 120, overflowX: "auto" }}>
                      {qKeys.map(q => {
                        const d = byQ[q];
                        const kH  = Math.round((d.keep     / maxQTotal) * 80);
                        const rH  = Math.round((d.remove   / maxQTotal) * 80);
                        const rsH = Math.round((d.reassign / maxQTotal) * 80);
                        const pH  = Math.round((d.pending  / maxQTotal) * 80);
                        return (
                          <div key={q} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, minWidth: 60 }}>
                            <div style={{ fontFamily: "var(--mono)", fontSize: 10, color: "var(--tx3)" }}>{d.total}</div>
                            <div style={{ display: "flex", flexDirection: "column-reverse", width: "100%", gap: 2 }}>
                              {pH  > 0 && <div style={{ height: pH,  background: "#FF9E18", borderRadius: "2px 2px 0 0" }} title={`Pending: ${d.pending}`}/>}
                              {kH  > 0 && <div style={{ height: kH,  background: "#6ABF4B" }} title={`Keep: ${d.keep}`}/>}
                              {rH  > 0 && <div style={{ height: rH,  background: "#dc3545" }} title={`Remove: ${d.remove}`}/>}
                              {rsH > 0 && <div style={{ height: rsH, background: "#00BBB4" }} title={`Reassign: ${d.reassign}`}/>}
                            </div>
                            <div style={{ fontFamily: "var(--mono)", fontSize: 10, color: "var(--tx3)", textAlign: "center" }}>{q}</div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ textAlign: "center", color: "var(--tx3)", fontSize: 12, padding: 24 }}>No historical data yet</div>
                  )}
                </div>
              </Card>

              {/* Summary table */}
              <Card title="Quarter Summary" right={`${qKeys.length} quarters`}>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        {["Quarter", "Total", "Keep", "Remove", "Reassign", "Pending", "Completion"].map(h => (
                          <th key={h} style={thStyle}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {qKeys.map((q, i) => {
                        const d   = byQ[q];
                        const rev = d.keep + d.remove + d.reassign;
                        const cp  = d.total > 0 ? Math.round((rev / d.total) * 100) : 0;
                        return (
                          <tr key={q} style={{ background: i % 2 === 0 ? "transparent" : "var(--s2)", borderBottom: "1px solid var(--b1)" }}>
                            <td style={{ padding: "9px 10px", fontFamily: "var(--mono)", fontWeight: 800, fontSize: 12, color: "#00B2E3" }}>{q}</td>
                            <td style={{ padding: "9px 10px", fontFamily: "var(--mono)", fontSize: 11 }}>{d.total}</td>
                            <td style={{ padding: "9px 10px", fontFamily: "var(--mono)", fontSize: 11, color: "#6ABF4B" }}>{d.keep}</td>
                            <td style={{ padding: "9px 10px", fontFamily: "var(--mono)", fontSize: 11, color: "#dc3545" }}>{d.remove}</td>
                            <td style={{ padding: "9px 10px", fontFamily: "var(--mono)", fontSize: 11, color: "#00BBB4" }}>{d.reassign}</td>
                            <td style={{ padding: "9px 10px", fontFamily: "var(--mono)", fontSize: 11, color: "#FF9E18" }}>{d.pending}</td>
                            <td style={{ padding: "9px 10px", fontFamily: "var(--mono)", fontSize: 11, color: "var(--tx3)" }}>{cp}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}
        </div>
      )}
    </div>
  );
}
